import { useState } from 'react'

import { useCreateFloor } from '../hooks/useCreateFloor'
import { useFloorPlanUploadProcessing } from '../hooks/useFloorPlanUploadProcessing'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { UPLOAD_ERROR_COMPONENTS, reportUploadError } from '@/hooks/useUpload'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { HiOutlineCheckCircle } from 'react-icons/hi'

import {
  Box,
  Center,
  Icon,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Spinner,
  Tooltip,
} from '@chakra-ui/react'

import { Floor } from '@/components/icons'
import { Modal } from '@/components/overlays'
import {
  Button,
  FacilityOptionLabel,
  FormInputControl,
  FormInputSelectControl,
} from '@/components/ui'
import { FormFileUploadControl } from '@/components/ui'
import { StyledFormInputControl } from '@/components/ui'
import { CreateFloorInput } from '@/graphql/generated/schemas'
import { createFacilityAddressFilter } from '@/utils/forms/optionBuilders'
import { getFacilityNoOptionsMessage } from '@/utils/forms/validation'

import { FloorFormInputsIProps } from '../types/types'
import {
  getErrorMessage,
  getSuccessMessage,
  isValidFileType,
} from '../utils/utils'
import { FloorPlanCropper } from './FloorPlanCropper'
import { PdfPageSelector } from './PdfPageSelector'

interface CreateFloorModalIProps {
  isOpen: boolean
  onClose: () => void
}

export const CreateFloorModal = ({
  isOpen,
  onClose,
}: CreateFloorModalIProps) => {
  const mode = useLightMode()
  const { t } = useTranslation()
  const { shouldEnableCreateFloor } = usePermissions()
  const [isLoading, setIsLoading] = useState(false)
  const { showSuccess, showError } = useToasts()
  const {
    facilityOptions,
    create,
    setFacilityName,
    isFacilitiesLoading,
    isFacilitiesError,
  } = useCreateFloor()
  const { trigger, watch, handleSubmit, register, control, errors } =
    useForm<FloorFormInputsIProps>({
      reValidateMode: 'onChange',
    })
  const fileUpload = watch('file')

  const {
    selectedPdfPage,
    setSelectedPdfPage,
    isPageConfirmed,
    confirmPage,
    unconfirmPage,
    selectedFile,
    isPdf,
    resetPdfPage,
    getFileForUpload,
    pdfSelectorRef,
    crop,
    setCrop,
    imagePreviewUrl,
    isPreviewLoading,
    shouldShowCropper,
    cropperRef,
    resetCrop,
  } = useFloorPlanUploadProcessing({ fileUpload })

  const onSubmit: SubmitHandler<FloorFormInputsIProps> = async (values) => {
    setIsLoading(true)
    const input: CreateFloorInput = {
      name: values.name,
      facilityId: values.facility?.value,
    }

    try {
      const file = await getFileForUpload(values.file?.item(0))
      await create(input, file)
      showSuccess(getSuccessMessage(values.name, 'create', file))
      onClose()
    } catch (e) {
      reportUploadError(e, UPLOAD_ERROR_COMPONENTS.CreateFloorModal)
      showError(getErrorMessage(values.name, 'create', e))
    }
    setIsLoading(false)
  }

  return (
    <Modal
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={onClose}
      scrollBehavior={isPdf || shouldShowCropper ? 'inside' : 'outside'}
      size='xl'
    >
      <ModalOverlay />
      <ModalContent bg='#fff'>
        <ModalHeader>
          <Box align-items='center' display='flex' m='20px auto'>
            <Box display='grid' placeItems='center'>
              <Icon as={Floor} boxSize='26px' strokeColor='#000' />
            </Box>
            <Box
              color={`text.primary.${mode}`}
              fontSize='26px'
              fontWeight='extrabold'
              letterSpacing='-0.4px'
              ml={3}
            >
              Create Floor
            </Box>
            <Spacer />
            <ModalCloseButton color='#000' opacity={0.4} position='initial' />
          </Box>
        </ModalHeader>
        <ModalBody p={0}>
          <Box px={6} py={2}>
            <Box mb={3}>
              <FormInputControl
                dataTestId='createFloor_modal_floorName'
                errorMessage={errors.name && errors.name.message}
                id='name'
                inputRef={register({
                  required: 'Floor name is required',
                })}
                isDisabled={isLoading}
                isInvalid={!!errors.name}
                label='Floor Name'
                placeholder='Enter floor name'
              />
            </Box>
            <Box mb={3}>
              <FormInputSelectControl
                control={control}
                dataTestId='createFloor_modal_facility'
                defaultValue={null}
                filterOption={createFacilityAddressFilter}
                formatOptionLabel={(option, meta) => (
                  <FacilityOptionLabel
                    isValueContext={meta?.context === 'value'}
                    option={option}
                  />
                )}
                id='facility'
                isClearable
                isDisabled={isLoading}
                isInvalid={!!errors.facility}
                isLoading={isFacilitiesLoading}
                isSearchable
                label='Facility'
                noOptionsMessage={({ inputValue }) =>
                  getFacilityNoOptionsMessage(
                    inputValue,
                    isFacilitiesError,
                    t('common:facility_search_error')
                  )
                }
                onInputChange={setFacilityName}
                options={facilityOptions}
                placeholder='Add a facility where this floor belongs to'
                rules={{ required: true }}
              />
            </Box>
            <StyledFormInputControl mb={3}>
              <Tooltip
                label='Supported file types include PNG, PDF, JPG, and JPEG'
                shouldWrapChildren
              >
                <FormFileUploadControl
                  accept='.png,.pdf,.jpg,.jpeg'
                  colorScheme={mode}
                  dataTestId='createFloor_modal_floorplan'
                  errorMessage={
                    fileUpload &&
                    !isValidFileType(fileUpload) &&
                    'File should be in .png, .pdf, .jpg, or .jpeg format'
                  }
                  id='file'
                  inputRef={register({ required: false })}
                  isDisabled={isLoading}
                  isInvalid={!isValidFileType(fileUpload)}
                  label='Floor Plan'
                  onFileRemoved={() => {
                    trigger(['file'])
                    resetPdfPage()
                    resetCrop()
                  }}
                  placeholder='Attach Floor Plan'
                />
              </Tooltip>
            </StyledFormInputControl>
            {isPdf && selectedFile && !isPageConfirmed && (
              <Box ref={pdfSelectorRef}>
                <PdfPageSelector
                  isDisabled={isLoading}
                  onConfirm={confirmPage}
                  onPageChange={setSelectedPdfPage}
                  pdfFile={selectedFile}
                  selectedPage={selectedPdfPage}
                />
              </Box>
            )}
            {isPreviewLoading && (
              <Center mt={4} py={8}>
                <Spinner color='blue.500' size='lg' />
              </Center>
            )}
            {shouldShowCropper && imagePreviewUrl && (
              <Box ref={cropperRef}>
                <FloorPlanCropper
                  crop={crop}
                  disabled={isLoading}
                  imageUrl={imagePreviewUrl}
                  onChangePage={isPdf ? unconfirmPage : undefined}
                  onCropChange={(_, percentCrop) => setCrop(percentCrop)}
                  onReset={resetCrop}
                />
              </Box>
            )}
          </Box>
        </ModalBody>
        <ModalFooter pb={6} py={4}>
          <Button mr={3} onClick={onClose} variant='secondary'>
            Cancel
          </Button>
          <Button
            isDisabled={
              !isValidFileType(fileUpload) || !shouldEnableCreateFloor
            }
            isLoading={isLoading}
            leftIcon={<HiOutlineCheckCircle size='24px' />}
            loadingText='Uploading Floor'
            onClick={handleSubmit(onSubmit)}
          >
            Create Floor
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
